import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, MapIcon, SparklesIcon, SwordIcon, ShieldIcon, HeartIcon, CrownIcon, UsersIcon, ChevronDownIcon, ChevronUpIcon, CircleDotIcon, ArrowRightIcon } from 'lucide-react';
const sectionHeader = (tag: string, title: string, accent: string, description?: string) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.8
}} className="text-center mb-16" data-id="element-263">
    <p className="text-[#d4a017]/40 tracking-[0.3em] text-[10px] uppercase mb-5 font-light" data-id="element-264">
      {tag}
    </p>
    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-#[f5f0e8] mb-6 tracking-tight" data-id="element-265">
      {title} <span className="text-[#d4a017]" data-id="element-266">{accent}</span>
    </h2>
    <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#d4a017]/30 to-transparent mx-auto mb-6" data-id="element-267"></div>
    {description && <p className="text-[#f5f0e8]/40 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-268">
        {description}
      </p>}
  </motion.div>;
/* ─── OVERVIEW ─── */
export function OverviewSection() {
  return <section id="overview" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-269">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/10 to-transparent" data-id="element-270"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-271">
        {sectionHeader('Section 01', 'Project', 'Overview', 'Understanding the immersive cultural experience that is redefining how we engage with heritage.')}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16" data-id="element-272">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-273">
            <div className="aspect-[4/3] relative overflow-hidden border border-[#d4a017]/10" data-id="element-274">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms immersive experience" className="w-full h-full object-cover" data-id="element-275" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/80 via-transparent to-transparent" data-id="element-276"></div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="space-y-6" data-id="element-277">
            <p className="text-[#f5f0e8]/70 text-lg leading-relaxed font-light" data-id="element-278">
              <strong className="text-[#d4a017] font-medium" data-id="element-279">
                Life in the Sikh Kingdoms
              </strong>{' '}
              is a 90-minute immersive dining and cultural experience at
              Toronto's Illuminarium. Guests step into the court of Maharaja
              Ranjit Singh through 360° projections, royal Punjabi cuisine,
              interactive technology, and multi-sensory storytelling.
            </p>
            <p className="text-[#f5f0e8]/50 leading-relaxed font-light" data-id="element-280">
              This is not a museum exhibit. It is a living, breathing portal to
              1825 Lahore — where history unfolds not behind glass, but all
              around you. Through walls that transform, floors that transport,
              and flavors that resurrect a golden age.
            </p>
            <p className="text-[#f5f0e8]/50 leading-relaxed font-light" data-id="element-281">
              Honoring the groundbreaking "Art of the Sikh Kingdoms" exhibition
              at the Royal Ontario Museum — the second most popular exhibition
              in ROM history — this experience brings that legacy into the
              immersive age.
            </p>
          </motion.div>
        </div>

        {/* Infographic strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-282">
          <div className="border border-[#d4a017]/15 bg-[#0f0f23]/60 p-6 text-center" data-id="element-283">
            <p className="font-serif text-3xl text-[#d4a017] mb-1" data-id="element-284">5</p>
            <p className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase font-light" data-id="element-285">
              Portals
            </p>
          </div>
          <div className="border border-[#d4a017]/15 bg-[#0f0f23]/60 p-6 text-center" data-id="element-286">
            <p className="font-serif text-3xl text-[#d4a017] mb-1" data-id="element-287">360°</p>
            <p className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase font-light" data-id="element-288">
              Projections
            </p>
          </div>
          <div className="border border-[#d4a017]/15 bg-[#0f0f23]/60 p-6 text-center" data-id="element-289">
            <p className="font-serif text-3xl text-[#d4a017] mb-1" data-id="element-290">90</p>
            <p className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase font-light" data-id="element-291">
              Minutes
            </p>
          </div>
          <div className="border border-[#d4a017]/15 bg-[#0f0f23]/60 p-6 text-center" data-id="element-292">
            <p className="font-serif text-3xl text-[#d4a017] mb-1" data-id="element-293">120</p>
            <p className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase font-light" data-id="element-294">
              Guests
            </p>
          </div>
        </motion.div>

        {/* Architecture Video */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mt-16" data-id="element-295">
          <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-296">
            <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-297">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/Kezdo9VonAo" title="Architecture of the Experience" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-298"></iframe>
            </div>
            <div className="p-6 border-t border-[#d4a017]/10" data-id="element-299">
              <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-300">
                Architecture of the Experience
              </h3>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-301">
                How the immersive space is designed — portals, projections,
                floor map, and spatial storytelling.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── GUEST JOURNEY ─── */
const journeyPhases = [{
  phase: 'Arrival',
  time: '0 min',
  description: 'Guests arrive at the Illuminarium Toronto. Ambient soundscape begins. Subtle saffron lighting guides them toward the entrance corridor.',
  detail: 'Check-in, welcome drink, initial sensory priming with ambient Sikh court music.'
}, {
  phase: 'Holding Room',
  time: '5 min',
  description: 'A transitional chamber where the story begins. Narration introduces Maharaja Ranjit Singh and the Sikh Empire. The portrait is revealed — fragmented, incomplete.',
  detail: 'Guests learn that five spirits must be awakened to restore the portrait. The quest begins.'
}, {
  phase: 'Main Room Entry',
  time: '10 min',
  description: 'The doors open to the full 360° immersive space. The living map of Punjab illuminates the floor. Five rivers glow. City nodes pulse.',
  detail: 'Guests orient themselves on the map. The first portal activation circle begins to glow.'
}, {
  phase: 'Portal Activations',
  time: '15–75 min',
  description: 'Five portals activate sequentially. Each portal is a complete cinematic chapter — visual, culinary, aromatic, and interactive.',
  detail: 'Akali Phula Singh → Lehna Singh Majithia → Hari Singh Nalwa → Fakir Azizuddin → Maharani Jind Kaur. Each restores a fragment of the portrait.'
}, {
  phase: 'Portrait Awakening',
  time: '75 min',
  description: 'All five fragments unite. The Lahore Darbar painting comes alive across the entire room. The court assembles. Maharaja Ranjit Singh appears.',
  detail: 'A unity gesture from all guests triggers the final reveal. The room transforms into the full court of Lahore.'
}, {
  phase: 'Exit & Legacy',
  time: '85 min',
  description: 'Guests exit through a reflection corridor. QR codes for digital souvenirs, merchandise, and sharing. The experience lives on.',
  detail: 'Photo moments, digital collectibles, merchandise area, and community sharing wall.'
}];
export function GuestJourneySection() {
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);
  return <section id="guest-journey" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-302">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1a1a2e]/5 to-transparent" data-id="element-303"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-4xl" data-id="element-304">
        {sectionHeader('Section 02', 'Guest', 'Journey', 'The complete arc from arrival to legacy — every phase designed for maximum immersion.')}

        <div className="relative" data-id="element-305">
          {/* Timeline line */}
          <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#d4a017]/30 via-[#d4a017]/10 to-transparent" data-id="element-306"></div>

          <div className="space-y-4" data-id="element-307">
            {journeyPhases.map((phase, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="relative pl-16 md:pl-20" data-id="element-308">
                {/* Timeline dot */}
                <div className="absolute left-4 md:left-6 top-6 w-4 h-4 rounded-full border-2 border-[#d4a017]/40 bg-[#0a0a0f] flex items-center justify-center" data-id="element-309">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#d4a017]" data-id="element-310"></div>
                </div>

                <button onClick={() => setExpandedPhase(expandedPhase === i ? null : i)} className="w-full text-left border border-#[d4a017]/10 hover:border-#[d4a017]/25 bg-#[0f0f23]/40 hover:bg-#[0f0f23]/60 p-6 transition-all duration-300" data-id="element-311">
                  <div className="flex items-start justify-between gap-4" data-id="element-312">
                    <div className="flex-1" data-id="element-313">
                      <div className="flex items-center gap-3 mb-2" data-id="element-314">
                        <span className="text-[#d4a017]/40 text-[10px] tracking-wider uppercase font-mono" data-id="element-315">
                          {phase.time}
                        </span>
                      </div>
                      <h4 className="font-serif text-xl text-[#f5f0e8] mb-2" data-id="element-316">
                        {phase.phase}
                      </h4>
                      <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-317">
                        {phase.description}
                      </p>
                    </div>
                    {expandedPhase === i ? <ChevronUpIcon className="w-4 h-4 text-[#d4a017] flex-shrink-0 mt-1" data-id="element-318" /> : <ChevronDownIcon className="w-4 h-4 text-[#d4a017]/30 flex-shrink-0 mt-1" data-id="element-319" />}
                  </div>
                </button>

                <AnimatePresence data-id="element-320">
                  {expandedPhase === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-321">
                      <div className="border border-t-0 border-[#d4a017]/10 bg-[#d4a017]/5 px-6 py-4" data-id="element-322">
                        <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-323">
                          {phase.detail}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}
/* ─── FIVE PORTALS ─── */
const portals = [{
  name: 'Akali Phula Singh',
  represents: 'Spirit & Fearlessness',
  tagline: 'The warrior-saint who embodied the Khalsa spirit',
  why: 'He represents the unbreakable spirit of the Sikh faith — the courage to stand for truth regardless of consequence. His portal awakens the fire within.',
  feel: 'Adrenaline, reverence, and the thundering energy of the Khalsa cavalry charging across the plains.',
  look: 'Crimson and gold projections. Dust storms. Cavalry formations. The Nishan Sahib flag rippling across walls.',
  sensory: 'Sound of war drums and Jaikara. Scent of gunpowder and earth. A bold, spiced bite — warrior’s fuel!',
  restoration: 'Restores the spirit fragment — the fire in the portrait’s eyes!'
}, {
  name: 'Lehna Singh Majithia',
  represents: 'Structure & Innovation',
  tagline: 'The master builder who modernized the empire',
  why: 'He represents the engineering genius of the Sikh Empire — the cannons, the forts, the infrastructure that made Lahore a wonder of the world.',
  feel: 'Precision, wonder, and the satisfaction of watching something magnificent being built before your eyes.',
  look: 'Blueprint projections. Architectural schematics. Cannon foundries. The walls of Lahore Fort rising from the floor.',
  sensory: 'Sound of hammers and forges. Scent of heated metal and sandalwood. A structured, layered bite — precision on a plate.',
  restoration: 'Restores the structure fragment — the architecture of the portrait’s background.'
}, {
  name: 'Hari Singh Nalwa',
  represents: 'Protection & Expansion',
  tagline: 'The legendary general who guarded the frontier',
  why: 'He represents the protective strength of the empire — the general so feared that his name alone held the Khyber Pass.',
  feel: 'Awe, safety, and the vastness of an empire that stretched from Lahore to Kabul.',
  look: 'Mountain panoramas. The Khyber Pass. Eagle-eye perspectives soaring over the frontier. Fortress walls.',
  sensory: 'Sound of mountain winds and eagle cries. Scent of pine and cold stone. A bold, protective bite — strength in every layer.',
  restoration: 'Restores the protection fragment — the borders and boundaries of the portrait.'
}, {
  name: 'Fakir Azizuddin',
  represents: 'Pluralism & Diplomacy',
  tagline: 'The Muslim diplomat who served the Sikh court',
  why: "He represents the pluralism of the Sikh Empire — a Muslim serving as chief diplomat in a Sikh court, embodying the empire's radical inclusivity.",
  feel: 'Warmth, connection, and the beauty of a court where faith was respected but never a barrier.',
  look: 'Diplomatic chambers. Treaty scrolls. The Koh-i-Noor diamond. Soft candlelight and rich fabrics.',
  sensory: 'Sound of courtly music and gentle conversation. Scent of rose and amber. A refined, diplomatic bite — elegance and balance.',
  restoration: "Restores the pluralism fragment — the diverse faces in the portrait's court."
}, {
  name: 'Maharani Jind Kaur',
  represents: 'Continuity & Resilience',
  tagline: 'The queen who fought to preserve the kingdom',
  why: 'She represents the continuity of the Sikh spirit — the mother, the queen, the political force who refused to let the empire die quietly.',
  feel: "Emotion, resilience, and the fierce love of a mother fighting for her son’s throne and her people's future.",
  look: 'Palace interiors. Candlelit chambers. Letters being written. The young Duleep Singh. Twilight over Lahore.',
  sensory: "Sound of a mother's lullaby and political whispers. Scent of jasmine and ink. A tender, complex bite — sweetness with depth.",
  restoration: 'Restores the continuity fragment — the heart of the portrait, completing the Lahore Darbar.'
}];
export function FivePortalsSection() {
  const [activePortal, setActivePortal] = useState(0);
  return <section id="five-portals" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-324">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-325"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-6xl" data-id="element-326">
        {sectionHeader('Section 03', 'The Five', 'Portals', 'Five historical figures. Five cinematic chapters. Five fragments of a shattered portrait.')}

        {/* Portal tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12" data-id="element-327">
          {portals.map((portal, i) => <button key={i} onClick={() => setActivePortal(i)} className={`px-4 py-2.5 text-xs tracking-[0.1em] uppercase transition-all duration-300 border ${activePortal === i ? 'bg-[#d4a017] text-[#0a0a0f] border-[#d4a017] font-medium' : 'border-#[d4a017]/20 text-#[f5f0e8]/40 hover:text-#[f5f0e8]/70 hover:border-#[d4a017]/40'}`} data-id="element-328">
              {portal.name.split(' ').pop()}
            </button>)}
        </div>

        {/* Active portal detail */}
        <AnimatePresence mode="wait" data-id="element-329">
          <motion.div key={activePortal} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.4
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/50" data-id="element-330">
            <div className="p-8 md:p-12" data-id="element-331">
              <div className="flex flex-col md:flex-row md:items-start gap-8 mb-10" data-id="element-332">
                <div className="flex-1" data-id="element-333">
                  <p className="text-[#d4a017]/50 text-[10px] tracking-[0.3em] uppercase mb-3 font-light" data-id="element-334">
                    Portal {activePortal + 1} of 5
                  </p>
                  <h3 className="font-serif text-3xl md:text-4xl text-[#f5f0e8] mb-2" data-id="element-335">
                    {portals[activePortal].name}
                  </h3>
                  <p className="text-[#d4a017] text-sm tracking-wider uppercase font-light mb-4" data-id="element-336">
                    {portals[activePortal].represents}
                  </p>
                  <p className="text-[#f5f0e8]/50 font-light italic" data-id="element-337">
                    {portals[activePortal].tagline}
                  </p>
                </div>
                <div className="w-24 h-24 md:w-32 md:h-32 border border-#[d4a017]/20 bg-#[d4a017]/5 flex items-center justify-center flex-shrink-0" data-id="element-338">
                  <span className="font-serif text-5xl md:text-6xl text-[#d4a017]/20" data-id="element-339">
                    {activePortal + 1}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-340">
                <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-341">
                  <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-342">
                    Why They Matter
                  </p>
                  <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-343">
                    {portals[activePortal].why}
                  </p>
                </div>
                <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-344">
                  <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-345">
                    What Guests Feel
                  </p>
                  <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-346">
                    {portals[activePortal].feel}
                  </p>
                </div>
                <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-347">
                  <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-348">
                    Visual World
                  </p>
                  <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-349">
                    {portals[activePortal].look}
                  </p>
                </div>
                <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-350">
                  <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-351">
                    Sound / Scent / Bite
                  </p>
                  <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-352">
                    {portals[activePortal].sensory}
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-[#d4a017]/10" data-id="element-353">
                <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-354">
                  Portrait Restoration
                </p>
                <p className="text-[#d4a017]/80 font-light italic" data-id="element-355">
                  {portals[activePortal].restoration}
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}
/* ─── STORY ARC ─── */
const arcPhases = [{
  label: 'Spirit',
  portal: 'Akali Phula Singh',
  emotion: 'Awakening'
}, {
  label: 'Structure',
  portal: 'Lehna Singh Majithia',
  emotion: 'Wonder'
}, {
  label: 'Protection',
  portal: 'Hari Singh Nalwa',
  emotion: 'Awe'
}, {
  label: 'Pluralism',
  portal: 'Fakir Azizuddin',
  emotion: 'Connection'
}, {
  label: 'Continuity',
  portal: 'Maharani Jind Kaur',
  emotion: 'Emotion'
}, {
  label: 'Court Return',
  portal: 'Lahore Darbar',
  emotion: 'Unity'
}];
export function StoryArcSection() {
  return <section id="story-arc" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-356">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-357">
        {sectionHeader('Section 04', 'Story', 'Arc', 'Five portals function as one connected cinematic arc — building toward the final court return.')}

        {/* Arc visualization */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mb-16" data-id="element-358">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-359">
            {arcPhases.map((phase, i) => <div key={i} className={`border p-5 text-center transition-all ${i === 5 ? 'border-[#d4a017]/40 bg-[#d4a017]/10' : 'border-[#d4a017]/10 bg-[#0f0f23]/40'}`} data-id="element-360">
                <p className="font-serif text-2xl text-[#d4a017]/30 mb-2" data-id="element-361">
                  {i + 1}
                </p>
                <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-362">
                  {phase.label}
                </p>
                <p className="text-[#f5f0e8]/30 text-[10px] tracking-wider uppercase mb-2" data-id="element-363">
                  {phase.portal}
                </p>
                <div className="w-8 h-px bg-[#d4a017]/20 mx-auto mb-2" data-id="element-364"></div>
                <p className="text-[#d4a017]/60 text-xs italic" data-id="element-365">
                  {phase.emotion}
                </p>
              </div>)}
          </div>
          {/* Arrow flow */}
          <div className="hidden lg:flex items-center justify-center mt-4 gap-1" data-id="element-366">
            {[0, 1, 2, 3, 4].map(i => <Fragment key={i} data-id="element-367">
                <div className="w-16 h-px bg-[#d4a017]/20" data-id="element-368"></div>
                <ArrowRightIcon className="w-3 h-3 text-[#d4a017]/30" data-id="element-369" />
              </Fragment>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10" data-id="element-370">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed text-center max-w-3xl mx-auto" data-id="element-371">
            The emotional arc builds from{' '}
            <strong className="text-[#d4a017]" data-id="element-372">awakening</strong> through{' '}
            <strong className="text-[#d4a017]" data-id="element-373">wonder</strong>,{' '}
            <strong className="text-[#d4a017]" data-id="element-374">awe</strong>, and{' '}
            <strong className="text-[#d4a017]" data-id="element-375">connection</strong>, reaching its
            emotional peak with{' '}
            <strong className="text-[#d4a017]" data-id="element-376">continuity</strong> — before
            culminating in the collective{' '}
            <strong className="text-[#d4a017]" data-id="element-377">unity</strong> of the final court
            return. Each portal restores a fragment of the shattered Lahore
            Darbar portrait, and only when all five are complete does the full
            painting come alive.
          </p>
        </motion.div>
      </div>
    </section>;
}
/* ─── FLOOR MAP ─── */
export function FloorMapSection() {
  return <section id="floor-map" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-378">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-379"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-380">
        {sectionHeader('Section 05', 'Living', 'Floor Map', 'The floor becomes a living map of Punjab — five rivers, city nodes, and activation circles guide the journey.')}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-381">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 aspect-square flex items-center justify-center relative overflow-hidden" data-id="element-382">
            <div className="absolute inset-0 opacity-10" data-id="element-383">
              <svg viewBox="0 0 400 400" className="w-full h-full" data-id="element-384">
                <path d="M 50, 200 Q 100, 100 200, 150 T 350, 200" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.5" data-id="element-385" />
                <path d="M 80, 250 Q 150, 150 250, 180 T 380, 220" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.4" data-id="element-386" />
                <path d="M 60, 300 Q 130, 200 230, 230 T 370, 260" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.3" data-id="element-387" />
                <path d="M 40, 170 Q 120, 80 220, 120 T 360, 170" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.3" data-id="element-388" />
                <path d="M 70, 140 Q 140, 60 240, 100 T 370, 140" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.2" data-id="element-389" />
                <circle cx="200" cy="180" r="20" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.6" data-id="element-390" />
                <circle cx="120" cy="220" r="12" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.4" data-id="element-391" />
                <circle cx="280" cy="200" r="12" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.4" data-id="element-392" />
                <circle cx="160" cy="140" r="10" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.3" data-id="element-393" />
                <circle cx="250" cy="260" r="10" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.3" data-id="element-394" />
              </svg>
            </div>
            <div className="text-center z-10 p-8" data-id="element-395">
              <MapIcon className="w-12 h-12 text-[#d4a017]/30 mx-auto mb-4" data-id="element-396" />
              <p className="text-[#f5f0e8]/30 text-sm font-light" data-id="element-397">
                Interactive Floor Map
              </p>
              <p className="text-[#f5f0e8]/20 text-xs font-light mt-1" data-id="element-398">
                Projected onto the venue floor
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="space-y-5" data-id="element-399">
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-400">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-401">
                Five Rivers
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-402">
                The five rivers of Punjab — Jhelum, Chenab, Ravi, Beas, and
                Sutlej — glow as luminous pathways across the floor, defining
                the geography of the empire.
              </p>
            </div>
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-403">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-404">
                City Nodes
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-405">
                Key cities — Lahore, Amritsar, Peshawar, Multan, Kashmir —
                appear as pulsing nodes. Each connects to a portal character and
                their historical significance.
              </p>
            </div>
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-406">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-407">
                Activation Circles
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-408">
                Five activation circles mark the portal locations. When guests
                gather on a circle, the portal sequence begins — the floor
                reacts, the walls transform, and the story unfolds.
              </p>
            </div>
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-409">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-410">
                Wayfinding
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-411">
                Luminous routes guide guests between portals. The map itself is
                a storytelling device — showing the empire's reach and the
                connections between its greatest figures.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
/* ─── FINAL REVEAL ─── */
export function FinalRevealSection() {
  return <section id="final-reveal" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-412">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-413">
        {sectionHeader('Section 06', 'The Final', 'Reveal', 'Five fragments unite. The Lahore Darbar awakens. The court assembles.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-414">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8" data-id="element-415">
            <p className="text-[#d4a017]/50 text-[10px] tracking-[0.2em] uppercase mb-4" data-id="element-416">
              Before
            </p>
            <div className="aspect-[3/2] bg-[#0a0a0f] border border-[#d4a017]/10 flex items-center justify-center mb-4" data-id="element-417">
              <div className="grid grid-cols-3 grid-rows-2 gap-2 p-8 w-full h-full" data-id="element-418">
                <div className="bg-[#d4a017]/5 border border-[#d4a017]/10" data-id="element-419"></div>
                <div className="bg-transparent" data-id="element-420"></div>
              </div>
            </div>
            <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-421">
              The portrait begins fragmented — five pieces missing, scattered
              across the portals.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="border border-[#d4a017]/30 bg-[#d4a017]/5 p-8" data-id="element-422">
            <p className="text-[#d4a017] text-[10px] tracking-[0.2em] uppercase mb-4" data-id="element-423">
              After
            </p>
            <div className="aspect-[3/2] bg-[#0a0a0f] border border-[#d4a017]/20 flex items-center justify-center mb-4" data-id="element-424">
              <div className="bg-gradient-to-br from-[#d4a017]/20 via-[#d4a017]/10 to-[#8b6914]/10 w-full h-full flex items-center justify-center" data-id="element-425">
                <CrownIcon className="w-16 h-16 text-[#d4a017]/40" data-id="element-426" />
              </div>
            </div>
            <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-427">
              The complete Lahore Darbar — alive, luminous, and projected across
              the entire room.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 space-y-4" data-id="element-428">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-429">
            <div data-id="element-430">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-431">
                Portrait Restoration
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-432">
                Each portal restores one fragment. The portrait assembles piece
                by piece across the evening — visible to all guests as a shared
                progress marker.
              </p>
            </div>
            <div data-id="element-433">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-434">
                Unity Gesture
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-435">
                A collective gesture from all guests triggers the final reveal —
                the room transforms, the court assembles, and Maharaja Ranjit
                Singh appears in full glory.
              </p>
            </div>
            <div data-id="element-436">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-437">
                Bonus Moment
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-438">
                A surprise portrait of Maharaja Ranjit Singh himself — a final
                emotional beat that brings the entire evening to its crescendo.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}